// A title's versions as the script review reads them (E31-S6-T1, E31-S6-T3; ADR-033): the answer
// shapes of the versions routes (apps/api/src/modules/learning/versions/view.ts, mirrored here and kept
// strict so a change shows), and the readings the screens make of them — which version a title's row
// speaks for, its stage in words, who did what and when, and whether the viewer drafted it.
//
// Nothing here decides what anyone may do. The distinct-person reading only chooses which controls to
// draw (ADR-033 decisions 2 and 4); the API decides again on every act and its 403 is shown as such.
import {
  Count,
  Id,
  IsoUtc,
  NonEmpty,
  PackVersion,
  Pages,
  Script,
  Sha256,
  TitleVersionState,
  Ulid,
} from "@waypoint/contracts";
import { z } from "zod";

const Act = z.strictObject({ by: NonEmpty, at: IsoUtc });

const VersionCore = {
  id: Ulid.optional(),
  number: Count,
  source: z.enum(["pack", "version"]),
  state: TitleVersionState,
  contentVersion: NonEmpty,
  pack: z.strictObject({ id: Id, version: PackVersion }),
  rendering: z.boolean(),
  hasRender: z.boolean(),
  created: Act.optional(),
  edited: Act.optional(),
  submitted: Act.optional(),
  sentBack: Act.extend({ reason: NonEmpty }).optional(),
  approved: Act.extend({ teachesChanged: z.boolean().optional() }).optional(),
  published: Act.optional(),
  retired: Act.optional(),
  cancelled: Act.optional(),
  /**
   * Why the last render failed (render_jobs.failure_reason, E31-S3-T3). The versions routes do not
   * send it yet; until they do, a failed render reads as failed with no reason.
   */
  renderFailure: z.strictObject({ reason: NonEmpty, at: IsoUtc.optional() }).optional(),
};

export const VersionSummary = z.strictObject({ ...VersionCore, served: z.boolean() });
export type VersionSummary = z.infer<typeof VersionSummary>;

export const VersionList = z.strictObject({ titleId: Id, versions: z.array(VersionSummary) });
export type VersionList = z.infer<typeof VersionList>;

export const VersionDetail = z.strictObject({
  ...VersionCore,
  titleId: Id,
  script: Script,
  stills: z.array(z.strictObject({ id: Id, sha256: Sha256 })),
  theme: Id,
  questions: z.string(),
  /**
   * The title's guide pages, in the pack the version was written against, for the draft editor's
   * citation check (E31-S6-T2). The route does not send it yet; until it does, the editor checks a
   * page's form only and the server's 400 marks one outside the range.
   */
  guidePages: Pages.optional(),
  render: z
    .strictObject({
      jobId: Id,
      sha256: Sha256,
      durationSeconds: z.number().positive(),
      scenes: z.array(z.strictObject({ index: Count, start: z.number(), end: z.number() })),
    })
    .optional(),
  /** The guide section and edition from the active pack's entry, for the intro card (ADR-036). */
  section: NonEmpty.max(120).optional(),
  guideEdition: NonEmpty.optional(),
});
export type VersionDetail = z.infer<typeof VersionDetail>;

/** Approve's 200 (E31-S2-T3): the version, the render it released, and what its publish would expire. */
export const Approved = z.strictObject({
  version: VersionDetail,
  renderJobId: Ulid,
  attestationsExpiring: Count,
});

export const versionsPath = (titleId: string) =>
  `/v1/content/titles/${encodeURIComponent(titleId)}/versions`;
export const versionPath = (id: string) => `/v1/content/versions/${encodeURIComponent(id)}`;
export const stillPath = (id: string, stillId: string) =>
  `${versionPath(id)}/stills/${encodeURIComponent(stillId)}`;
export const mediaPath = (id: string) => `${versionPath(id)}/media`;
export const captionsPath = (id: string) => `${versionPath(id)}/captions`;

type Version = VersionSummary | VersionDetail;

/** The version a title's row speaks for: the newest that is not cancelled or retired. */
export function currentOf(versions: readonly VersionSummary[]): VersionSummary | undefined {
  return versions.find(
    (v) => v.id !== undefined && v.state !== "cancelled" && v.state !== "retired",
  );
}

/** Approved, with no render in flight and none held: the render was asked for and failed. */
export function renderFailed(v: Version): boolean {
  return v.state === "approved" && !v.rendering && !v.hasRender;
}

export type Stage =
  | "draft"
  | "sent-back"
  | "waiting"
  | "held"
  | "rendering"
  | "ready"
  | "failed"
  | "live"
  | "retired"
  | "cancelled";

/** Where a version stands, in the mockup's terms. `held` is the title's open contradictions. */
export function stageOf(v: Version, held: readonly string[]): Stage {
  switch (v.state) {
    case "draft":
      return v.sentBack === undefined ? "draft" : "sent-back";
    case "in-review":
      return held.length > 0 ? "held" : "waiting";
    case "approved":
      return v.hasRender ? "ready" : v.rendering ? "rendering" : "failed";
    case "published":
      return "live";
    case "retired":
      return "retired";
    case "cancelled":
      return "cancelled";
  }
}

/** Whether the viewer created, submitted or edited the version: they may not approve or publish it. */
export function isDrafter(v: Version, userId: string): boolean {
  return [v.created, v.submitted, v.edited].some((act) => act?.by === userId);
}

export type ActKind =
  | "created"
  | "edited"
  | "submitted"
  | "sentBack"
  | "approved"
  | "published"
  | "retired"
  | "cancelled";

export interface TrailEntry {
  readonly kind: ActKind;
  readonly by: string;
  readonly at: string;
  /** The send-back's note. */
  readonly note?: string | undefined;
}

const KINDS: readonly ActKind[] = [
  "created",
  "edited",
  "submitted",
  "sentBack",
  "approved",
  "published",
  "retired",
  "cancelled",
];

/** The version's history, oldest first: one entry per act the row records. */
export function trailOf(v: Version): TrailEntry[] {
  const entries: TrailEntry[] = [];
  for (const kind of KINDS) {
    const act = v[kind];
    if (act === undefined) continue;
    entries.push({
      kind,
      by: act.by,
      at: act.at,
      ...(kind === "sentBack" && v.sentBack !== undefined ? { note: v.sentBack.reason } : {}),
    });
  }
  // Stable: two acts at the same instant keep the order above.
  return entries.sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
}

/** The last act on the version, for a row's actor line. */
export function lastActOf(v: Version): TrailEntry | undefined {
  return trailOf(v).at(-1);
}

/** About how long the narration takes, at the mockup's 2.5 words a second. */
export function wordsOf(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}
export const secondsFor = (words: number) => Math.round(words / 2.5);
